import { useEffect, useMemo, useState } from "react";

const DEFAULT_LOGIN = `#!/bin/bash
# Login-Skript (läuft im Kontext des angemeldeten Nutzers)
set -euo pipefail
`;

function asScriptList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ScriptPanel({ title, scripts, disabled, onChange }) {
  const [open, setOpen] = useState(0);

  const updateAt = (index, patch) => {
    onChange(scripts.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeAt = (index) => {
    const next = scripts.filter((_, i) => i !== index);
    onChange(next);
    setOpen((prev) => Math.min(prev, Math.max(0, next.length - 1)));
  };

  const add = () => {
    const next = [
      ...scripts,
      {
        filename: `script-${scripts.length + 1}.sh`,
        scriptText: DEFAULT_LOGIN,
      },
    ];
    onChange(next);
    setOpen(next.length - 1);
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <label className="form-label mb-0">{title}</label>
        <button type="button" className="btn btn-sm btn-primary" disabled={disabled} onClick={add}>
          + Skript hinzufügen
        </button>
      </div>
      {!scripts.length ? (
        <div className="border rounded p-3 bg-light">
          <p className="text-muted small mb-2">Noch keine Skripte in dieser Gruppe.</p>
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={add}>
            Erstes Skript anlegen
          </button>
        </div>
      ) : (
        <div className="list-group">
          {scripts.map((row, index) => {
            const isOpen = open === index;
            return (
              <div key={`${row.filename}-${index}`} className="list-group-item">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0 flex-grow-1 text-start"
                    disabled={disabled}
                    onClick={() => setOpen(isOpen ? -1 : index)}
                  >
                    {row.filename || `Skript ${index + 1}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={disabled}
                    onClick={() => removeAt(index)}
                  >
                    Entfernen
                  </button>
                </div>
                {isOpen ? (
                  <div className="mt-2">
                    <label className="form-label form-label-sm">Dateiname</label>
                    <input
                      className="form-control form-control-sm mb-2"
                      disabled={disabled}
                      value={row.filename || ""}
                      onChange={(e) => updateAt(index, { filename: e.target.value })}
                      placeholder="login.sh"
                    />
                    <label className="form-label form-label-sm">Inhalt</label>
                    <textarea
                      className="form-control font-monospace"
                      style={{ minHeight: 180, fontSize: 13 }}
                      disabled={disabled}
                      spellCheck={false}
                      value={row.scriptText || ""}
                      onChange={(e) => updateAt(index, { scriptText: e.target.value })}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LoginScriptsEditor({ fields, onChange, disabled }) {
  const loginScripts = useMemo(
    () => asScriptList(fields?.LoginScripts ?? fields?.loginscripts),
    [fields?.LoginScripts, fields?.loginscripts],
  );
  const logoutScripts = useMemo(
    () => asScriptList(fields?.LogoutScripts ?? fields?.logoutscripts),
    [fields?.LogoutScripts, fields?.logoutscripts],
  );
  const enableMcx = fields?.enableMcxLoginScripts !== false && fields?.enableMcxLoginScripts !== "false";
  const logoutEnabled = fields?.LogOutScriptEnabled !== false && fields?.LogOutScriptEnabled !== "false";

  const patch = (next) => onChange({ ...fields, ...next });

  // Beim ersten Öffnen ohne Skripte ein Login-Skript anlegen, damit Speichern möglich ist
  useEffect(() => {
    if (disabled) return;
    if (loginScripts.length || logoutScripts.length) return;
    patch({
      LoginScripts: [{ filename: "login.sh", scriptText: DEFAULT_LOGIN }],
      LogoutScripts: logoutScripts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur initial seeden
  }, [disabled]);

  return (
    <div>
      <div className="alert alert-info py-2 small">
        Klassische MCX-Login-Skripte (Profil-Payload <code>com.apple.mcxloginscripts</code>).
        Xavier setzt <code>EnableMCXLoginScripts=true</code> und <code>MCXScriptTrust=Anonymous</code>
        (ohne Trust werden Skripte oft still verworfen). Skripte liegen nicht als Datei im Finder,
        sondern im Profil; sie laufen erst beim nächsten Login. In macOS-UIs kann Boolean als{" "}
        <code>1</code> erscheinen — das ist normal.
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="enableMcxLoginScripts"
          disabled={disabled}
          checked={enableMcx}
          onChange={(e) => patch({ enableMcxLoginScripts: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="enableMcxLoginScripts">
          EnableMCXLoginScripts mitschreiben
        </label>
      </div>

      <ScriptPanel
        title="Login-Skripte"
        scripts={loginScripts}
        disabled={disabled}
        onChange={(LoginScripts) => patch({ LoginScripts, loginscripts: LoginScripts })}
      />

      <div className="form-check mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id="LogOutScriptEnabled"
          disabled={disabled}
          checked={logoutEnabled}
          onChange={(e) => patch({ LogOutScriptEnabled: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="LogOutScriptEnabled">
          Logout-Skripte aktiv
        </label>
      </div>

      <ScriptPanel
        title="Logout-Skripte"
        scripts={logoutScripts}
        disabled={disabled}
        onChange={(LogoutScripts) => patch({ LogoutScripts, logoutscripts: LogoutScripts })}
      />
    </div>
  );
}
